import React from 'react';
import { Box, Loader } from 'braid-design-system';
import { MobileScrollView } from './components/MobileScrollView';
import { DesktopCarousel } from './components/DesktopCarousel';
import { useCarouselState } from './hooks/useCarouselState';
import { useCarouselNavigation } from './hooks/useCarouselNavigation';
import { CAROUSEL_CONSTANTS, type CarouselItem } from './utils/carouselUtils';

export const Carousel = ({
  items,
  maxItemsToShow = CAROUSEL_CONSTANTS.DEFAULT_MAX_ITEMS,
}: {
  items: CarouselItem[];
  maxItemsToShow?: number;
}) => {
  const carouselState = useCarouselState({ items, maxItemsToShow });
  const navigation = useCarouselNavigation({
    currentChunkIndex: carouselState.currentChunkIndex,
    isAnimating: carouselState.isAnimating,
    isFirstChunk: carouselState.isFirstChunk,
    isLastChunk: carouselState.isLastChunk,
    animateAndChangeChunk: carouselState.animateAndChangeChunk,
  });

  // Show loader until responsive value is determined to prevent content flash
  if (!carouselState.isResponsiveValueReady) {
    return (
      <Box 
        background="neutralSoft"
        borderRadius="large"
        display="flex" 
        justifyContent="center" 
        alignItems="center"
        paddingY="xlarge"
        style={{ minHeight: '200px' }}
      >
        <Loader aria-label="Loading carousel content" delayVisibility size="large" />
      </Box>
    );
  }

  // Conditional rendering based on device type
  if (carouselState.isMobile) {
    return <MobileScrollView items={items} />;
  }

  return (
    <DesktopCarousel
      chunks={carouselState.chunks}
      currentChunkIndex={carouselState.currentChunkIndex}
      isAnimating={carouselState.isAnimating}
      animatingToIndex={carouselState.animatingToIndex}
      animationDirection={carouselState.animationDirection}
      totalChunks={carouselState.totalChunks}
      chunkSize={carouselState.chunkSize}
      onLeftClick={navigation.moveItemsLeft}
      onRightClick={navigation.moveItemsRight}
      onPaginationClick={navigation.moveToChunk}
    />
  );
};
